<template>
    <div class="main-page">
        <div class="main-page__form-container">
            <div class="tool-description">
                <h1>{{ $t(pageLanguage.mainPageTitle) }}</h1>
                <h5>{{ $t(pageLanguage.toolDescription) }}</h5>
                <p>{{ $t('AgeOfRetirementTooltip') }}</p>
            </div>
            <form class="main-page__form" @submit.prevent="nextPage">
                <div class="selector selector--birthday required">
                    <div class="selector__label">{{ $t('EnterBirthday') }}<span>*</span></div>
                    <div class="selectors__container">
                        <SelectComponent
                            id="qa-select-month"
                            :options="monthOptions"
                            @onSelectChange="handleSelect"
                            type="month"
                            :error="errors.month"
                            :errorMessage="'Select a Month'" />
                        <SelectComponent
                            id="qa-select-day"
                            :options="dayOptions"
                            @onSelectChange="handleSelect"
                            type="day"
                            :error="errors.day"
                            :errorMessage="'Select a Day'" />
                        <SelectComponent
                            id="qa-select-year"
                            :options="yearOptions"
                            @onSelectChange="handleSelect"
                            type="year"
                            :error="errors.year"
                            :errorMessage="'Select a Year'" />
                    </div>
                </div>
                <div class="selector selector--age required">
                    <div class="selector__label">{{ $t('AgeOfRetirement') }}<span>*</span></div>
                    <div class="selectors__container">
                        <SelectComponent
                            id="qa-select-retirement"
                            class="block"
                            :options="retirementOptions"
                            @onSelectChange="handleSelect"
                            type="retirement" />
                    </div>
                </div>
                <div class="selector--button">
                    <button
                        id="qa-button-submit"
                        class="mds-button mds-button--primary" 
                        type="submit">
                            {{ $t('SubmitButton') }}
                        </button>
                </div>
            </form>
        </div>
        <div v-if="showChart" class="main-page__assets-allocation mds-card mds-card--large">
            <AssetsAllocation
                :year="Number(year)"
                :desiredRetirementYears="Number(retirement)"
                @onTdfRetirementYearChange="setTdfRetirementYear"
            />
            <div class="main-page__target-date">
                <div class="fund-allocations">
                    <h2 class="fund-allocations__title">{{ $t('FundAllocations') }}</h2>
                    <TableComponent :data="fixedIncomeOptions" class="fund-allocations__table" />
                    <TableComponent :data="equityOptions" class="fund-allocations__table" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import SelectComponent from "../components/Select";
import AssetsAllocation from "../components/AssetsAllocation";
import TableComponent from "../components/Table";
import Data from "../assets/data.json";
import MstarData from "../assets/mstarData.json";

const MIN_AGE_FOR_AGE = 16;
const FIRST_YEAR = 1920;
const DEFAULT_AGE_RETIREMENT = 65;
const MIN_AGE_FOR_RETIREMENT = 45;
const MAX_AGE_FOR_RETIREMENT = 80;

export default {
    data () {
        const retirementOptions = this.getRetirementAges();

        return {
            routeToUse: this.$router.currentRoute.path.toLowerCase().includes('morningstarlifetimeindexfunds') ? 'mstar' : '',
            year: null,
            month: null,
            day: null,
            retirement: _.find(retirementOptions, "default").value,
            errorMessage: "",
            showChart: false,
            errors: {
                year: null,
                month: null,
                day: null
            },
            tdfRetirementYear: null,
            retirementOptions
        };
    },
    computed: {
        fixedIncomeOptions () {
            return this.getItemsByGroup("fixed-income-options");
        },
        dataToUse () {
            return this.routeToUse === 'mstar' ? MstarData : Data;
        },
        equityOptions () {
            return this.getItemsByGroup("equity-options");
        },
        yearOptions () {
            return [
                { value: null, label: this.$t("Year"), disabled: true },
                ...this.getYears()
            ];
        },
        monthOptions () {
            return [
                { value: null, label: this.$t("Month"), disabled: true },
                { value: "1", label: this.$t("JAN") },
                { value: "2", label: this.$t("FEB") },
                { value: "3", label: this.$t("MAR") },
                { value: "4", label: this.$t("APR") },
                { value: "5", label: this.$t("MAY") },
                { value: "6", label: this.$t("JUN") },
                { value: "7", label: this.$t("JUL") },
                { value: "8", label: this.$t("AUG") },
                { value: "9", label: this.$t("SEP") },
                { value: "10", label: this.$t("OCT") },
                { value: "11", label: this.$t("NOV") },
                { value: "12", label: this.$t("DEC") }
            ];
        },
        dayOptions () {
            const year = this.year || this.yearOptions[1].value;
            const month = this.month || this.monthOptions[1].value;
            const daysInMonth = new Date(year, month, 0).getDate();
            const days = _
                .chain(daysInMonth)
                .times(String)
                .map(v => {
                    const value = String(Number(v) + 1);

                    return {
                        value,
                        label: value
                    };
                })
                .value();

            return [{ value: null, label: this.$t("Day"), disabled: true }, ...days];
        },
        pageLanguage () {
            const defaultLanguage = {
                mainPageTitle: 'MainPageTitle',
                toolDescription: 'ToolDescription',
            };
            const mstarLanguage = {
                mainPageTitle: 'MStarMainPageTitle',
                toolDescription: 'MStarToolDescription',
            };
            const languageToUse = this.routeToUse === 'mstar' ? mstarLanguage : {};
            return Object.assign({}, defaultLanguage, languageToUse);
        }
    },
    components: {
        SelectComponent,
        AssetsAllocation,
        TableComponent
    },
    watch: {
        retirementOptions (options) {
            this.retirement = _.find(this.options, "default").value;
        },
        $route(to, from) {
            this.routeToUse = to.path.toLowerCase().includes('morningstarlifetimeindexfunds') ? 'mstar' : '';
        }
    },
    methods: {
        setTdfRetirementYear (value) {
            this.tdfRetirementYear = value;
        },
        getItemsByGroup (groupId) {
            const dataToUse = this.dataToUse;
            const { xDomain, y1AxisData, groups } = dataToUse.fundAllocations;
            const options = _.filter(groups, group => group.id === groupId);
            const defaultOption = [
                _.merge({
                    text: "Equity Options",
                    color: ""
                }, {
                    text: options[0] && options[0].name
                })
            ];

            return {
                headings: defaultOption.concat(_.map(xDomain, (x, i) => ({
                    text: x.label,
                    color: i === this.tdfRetirementYear ? "#F2F2F2" : ""
                }))),
                rows: _
                    .chain(y1AxisData)
                    .filter(y => y.group === groupId)
                    .map(y => {
                        const defaultOption = [
                            {
                                text: y.name,
                                scoped: true,
                                color: y.color
                            }
                        ];

                        return defaultOption.concat(y.values.map((v, i) => ({
                            text: `${v}%`,
                            color: i === this.tdfRetirementYear ? y.color : "",
                            fontColor: i === this.tdfRetirementYear ? y.fontColor : ""
                        })));
                    })
                    .value()
            };
        },
        handleSelect (data) {
            this[data.type] = data.item.value;
        },
        getYears () {
            const date = new Date();
            const LAST_VALID_YEAR = date.getUTCFullYear() - MIN_AGE_FOR_AGE;

            return _
                .chain(LAST_VALID_YEAR - FIRST_YEAR)
                .times(String)
                .map(v => {
                    const value = String(FIRST_YEAR + Number(v));

                    return {
                        value,
                        label: value
                    };
                })
                .reverse()
                .value();
        },
        getRetirementAges () {
            return _
                .chain(MAX_AGE_FOR_RETIREMENT - MIN_AGE_FOR_RETIREMENT)
                .times(String)
                .map(v => {
                    const value = String(MIN_AGE_FOR_RETIREMENT + Number(v));
                    const label = value + " yrs. old";

                    if (value === String(DEFAULT_AGE_RETIREMENT)) {
                        return {
                            value,
                            default: true,
                            label
                        };
                    }

                    return {
                        value,
                        label
                    };
                })
                .value();
        },
        nextPage () {
            if (this.checkErrors()) {
                return;
            }
            this.showChart = true;
        },
        checkErrors () {
            let haveErrors = false;

            this.errors = _.mapValues(this.errors, (value, field) => {
                if (!this[field]) {
                    haveErrors = true;
                }

                return !this[field];
            });

            this.errorMessage = haveErrors ? this.$t("BirthdayError") : null;

            return haveErrors;
        }
    }
};
</script>

<style lang="scss" scoped>
.main-page {
    text-align: center;
    h1 {
        margin: $mds-space-4-x 0 0;
    }

    h5 {
        margin: $mds-space-1-and-a-half-x 0 0;
    }

    .tool-description {
        p {
            @include mds-body-text-s();
            margin: $mds-space-1-and-a-half-x 0 0;
        }
    }
    .selector {
        position: relative;
        text-align: left;

        &.required {
            span {
                color: $mds-color-red-50;
            }
        }

        &--birthday {

            .mds-form__select {
                &:last-child {
                    margin-bottom: $mds-space-3-x;
                }
            }
        }

        &__label {
            margin-bottom: $mds-space-1-and-a-half-x;
        }
    }

    &__form {
        margin: 2.875rem 0 $mds-space-4-x 0;

        &-container {
            color: $mds-color-black;
            text-align: center;
            margin: 0 $mds-space-4-x;
        }

        .mds-form__select {
            margin-bottom: $mds-space-4-x;
            max-width: initial;
            display: block;
        }
    }

    &__target-date {
        .fund-allocations {
            margin-top: $mds-space-8-x;

            &__title {
                margin: 0;
                text-align: left;
            }

            &__table {
                &:first-of-type {
                    margin: $mds-space-3-x 0 0;
                }
                
                &:last-of-type {
                    padding-top: $mds-space-3-x;
                }
            }
        }
    }

    &__assets-allocation {
        display: block;
        margin: 0 10px;
        padding: 0 10px;
    }

    @media #{$bp-desktop} {
        h5 {
            margin: $mds-space-3-x 0 0;
        }

        .selector {
            margin-right: $mds-space-1-and-a-half-x;

            &--birthday {
                flex: 2;

                .mds-form__select {
                    &:last-child {
                        margin-bottom: 0;
                    }
                }
            }

            &--age {
                flex: 1;
            }

            &--button {
                padding-top: $mds-space-4-x;
            }
        }

        .selectors__container {
            display: flex;

            & > div {
                flex: 2;
                margin-right: $mds-space-1-and-a-half-x;
            }

            div:first-child {
                flex: 3;
            }
        }

        &__form {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin: $mds-space-4-x 0 0;

            &-container {
                border-bottom: 1px solid transparent;
                margin: 0 auto $mds-space-4-x auto;
                text-align: center;
                max-width: 660px;
            }

            .mds-form__select {
                margin-bottom: 0;
            }
        }

        &__assets-allocation {
            padding: 0 $mds-space-8-x;
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/routes/MainPage.vue